"use client";

import { c as _c } from "react/compiler-runtime";
import { useT } from "@/lib/i18n";
import { CDN_BASE } from "@/lib/image-url";
import { useEffect, useRef, useState } from "react";
export const CHARACTER_THEMES = ["ironclad", "silent", "defect", "necrobinder", "regent"] as const;
export type CharacterTheme = (typeof CHARACTER_THEMES)[number];
type Mode = "light" | "dark" | "character";
const CHARACTER_NAMES: Record<CharacterTheme, string> = {
  ironclad: "Ironclad",
  silent: "Silent",
  defect: "Defect",
  necrobinder: "Necrobinder",
  regent: "Regent"
};
function isCharacter(value: string | null): value is CharacterTheme {
  return (CHARACTER_THEMES as readonly string[]).includes(value ?? "");
}
function readCurrent(): {
  mode: Mode;
  character: CharacterTheme;
} {
  const attr = document.documentElement.getAttribute("data-theme");
  let character: CharacterTheme = "ironclad";
  try {
    const remembered = localStorage.getItem("theme-character");
    if (isCharacter(remembered)) character = remembered;
  } catch {
    /* storage unavailable */
  }
  if (isCharacter(attr)) return {
    mode: "character",
    character: attr
  };
  return {
    mode: attr === "light" ? "light" : "dark",
    character
  };
}
function applyTheme(value: "light" | "dark" | CharacterTheme) {
  document.documentElement.setAttribute("data-theme", value);
  document.documentElement.classList.toggle("dark", value !== "light");
  try {
    localStorage.setItem("theme", value);
    if (isCharacter(value)) localStorage.setItem("theme-character", value);
  } catch {
    /* private mode / storage disabled */
  }
}
const sun = <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>;
const moon = <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
  </svg>;
const swords = <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14.5 17.5 3 6V3h3l11.5 11.5" />
    <path d="M13 19l6-6" />
    <path d="M16 16l4 4" />
    <path d="M19 21l2-2" />
    <path d="M9.5 6.5 21 18" />
  </svg>;
function characterIcon(key: CharacterTheme) {
  return `${CDN_BASE}/ui/characters/character_icon_${key}.webp`;
}

/** Light / Dark / Character theme picker. Flips `data-theme` on <html> and
 * the Tailwind `dark` class, persists the choice, and the inline script in
 * the root layout applies it before first paint. Palettes live in
 * globals.css: `:root[data-theme="light"]` and one block per character.
 *
 * variants: "icon" = compact nav-cluster button opening a popover
 * (desktop); "segmented" = a "Theme" row for the mobile drawer. */
export default function ThemeToggle(t0) {
  const $ = _c(58);
  const {
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "icon" : t1;
  const t = useT();
  const [mode, setMode] = useState("dark");
  const [character, setCharacter] = useState("ironclad");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const current = readCurrent();
      setMode(current.mode);
      setCharacter(current.character);
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[2] !== open) {
    t4 = () => {
      if (!open) {
        return;
      }
      const onDown = e => {
        if (!wrapRef.current?.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      const onKey = e_0 => {
        if (e_0.key === "Escape") {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", onDown);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onDown);
        document.removeEventListener("keydown", onKey);
      };
    };
    t5 = [open];
    $[2] = open;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] !== character) {
    t6 = next => {
      setMode(next);
      applyTheme(next === "character" ? character : next);
    };
    $[5] = character;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const chooseMode = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = next_0 => {
      setCharacter(next_0);
      setMode("character");
      applyTheme(next_0);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const chooseCharacter = t7;
  let t8;
  if ($[8] !== chooseMode || $[9] !== mode) {
    t8 = (value, label, icon, compact) => <button key={value} type="button" onClick={() => chooseMode(value)} aria-pressed={mode === value} className={`flex items-center gap-1.5 rounded-md ${compact ? "px-2.5 py-1.5" : "px-3 py-1.5"} text-sm font-medium transition-colors ${mode === value ? "bg-[var(--accent-gold)] text-on-accent" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}>{icon}{label}</button>;
    $[8] = chooseMode;
    $[9] = mode;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const segment = t8;
  let t9;
  if ($[11] !== t) {
    t9 = t("Pick a character");
    $[11] = t;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== character || $[14] !== mode) {
    t10 = CHARACTER_THEMES.map(key => {
      const active = mode === "character" && character === key;
      return <button key={key} type="button" role="radio" aria-checked={active} title={CHARACTER_NAMES[key]} aria-label={CHARACTER_NAMES[key]} onClick={() => chooseCharacter(key)} className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${active ? "border-[var(--accent-gold)] bg-[var(--accent-gold)]/15" : "border-[var(--border-subtle)] bg-[var(--bg-primary)] hover:border-[var(--border-accent)]"}`}><img src={characterIcon(key)} alt="" width={24} height={24} className="h-6 w-6 object-contain" loading="lazy" /></button>;
    });
    $[13] = character;
    $[14] = mode;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = <div role="radiogroup" aria-label={t9} className="flex items-center gap-1.5">{t10}</div>;
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const characterRow = t11;
  let t12;
  if ($[19] !== segment || $[20] !== t) {
    t12 = compact_0 => <div className="flex gap-1 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-1">{segment("light", t("Light"), sun, compact_0)}{segment("dark", t("Dark"), moon, compact_0)}{segment("character", t("Character"), swords, compact_0)}</div>;
    $[19] = segment;
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const segments = t12;
  if (variant === "segmented") {
    let t13;
    if ($[22] !== t) {
      t13 = t("Theme");
      $[22] = t;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    let t14;
    if ($[24] !== t13) {
      t14 = <span className="text-lg font-semibold text-[var(--text-primary)]">{t13}</span>;
      $[24] = t13;
      $[25] = t14;
    } else {
      t14 = $[25];
    }
    let t15;
    if ($[26] !== segments) {
      t15 = segments(true);
      $[26] = segments;
      $[27] = t15;
    } else {
      t15 = $[27];
    }
    let t16;
    if ($[28] !== t14 || $[29] !== t15) {
      t16 = <div className="flex items-center justify-between gap-3">{t14}{t15}</div>;
      $[28] = t14;
      $[29] = t15;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    let t17;
    if ($[31] !== character || $[32] !== characterRow || $[33] !== mode) {
      t17 = mode === "character" && <div className="mt-3 flex items-center justify-between gap-3"><span className="text-sm text-[var(--text-secondary)]">{CHARACTER_NAMES[character]}</span>{characterRow}</div>;
      $[31] = character;
      $[32] = characterRow;
      $[33] = mode;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    let t18;
    if ($[35] !== t16 || $[36] !== t17) {
      t18 = <div className="px-5 py-4">{t16}{t17}</div>;
      $[35] = t16;
      $[36] = t17;
      $[37] = t18;
    } else {
      t18 = $[37];
    }
    return t18;
  }
  let t13;
  if ($[38] !== character || $[39] !== mode) {
    t13 = mode === "character" ? <img src={characterIcon(character)} alt="" width={20} height={20} className="h-5 w-5 object-contain" /> : mode === "light" ? sun : moon;
    $[38] = character;
    $[39] = mode;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const triggerIcon = t13;
  let t14;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setOpen(_temp);
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t) {
    t15 = t("Theme");
    $[42] = t;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== open || $[45] !== t15 || $[46] !== triggerIcon) {
    t16 = <button type="button" onClick={t14} aria-haspopup="dialog" aria-expanded={open} aria-label={t15} className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors">{triggerIcon}</button>;
    $[44] = open;
    $[45] = t15;
    $[46] = triggerIcon;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== character || $[49] !== characterRow || $[50] !== mode || $[51] !== open || $[52] !== segments || $[53] !== t) {
    t17 = open && <div role="dialog" aria-label={t("Theme")} className="absolute right-0 top-11 z-50 w-max min-w-[16rem] rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3 shadow-2xl shadow-scrim/50"><div className="mb-2 text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">{t("Theme")}</div>{segments(false)}{mode === "character" && <div className="mt-3 border-t border-[var(--border-subtle)] pt-3"><div className="mb-2 flex items-center justify-between text-xs text-[var(--text-muted)]"><span className="uppercase tracking-wider">{t("Pick a character")}</span><span className="text-[var(--text-secondary)]">{CHARACTER_NAMES[character]}</span></div>{characterRow}</div>}</div>;
    $[48] = character;
    $[49] = characterRow;
    $[50] = mode;
    $[51] = open;
    $[52] = segments;
    $[53] = t;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== t16 || $[56] !== t17) {
    t18 = <div ref={wrapRef} className="relative">{t16}{t17}</div>;
    $[55] = t16;
    $[56] = t17;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  return t18;
}
function _temp(v) {
  return !v;
}